/**
 * src/sync/api.js — thin HTTP wrapper around the .NET shadow-write API.
 *
 * The single exported function `postSync()` does:
 *   1. Resolve the base URL from `VITE_API_BASE_URL` (default: http://127.0.0.1:5050).
 *   2. Obtain a Firebase ID token (Bearer JWT) from the current user.
 *   3. POST the envelope as JSON with an AbortController-driven timeout.
 *   4. Throw on network failure / non-2xx so the caller (shadowWriter / outbox)
 *      can route the envelope to the outbox queue.
 *
 * Auth model (Etap 3+)
 * --------------------
 * Previously this module shipped an `X-Dev-Sync-Token` read from
 * `VITE_DEV_SYNC_TOKEN`, which was bundled into the production JS and visible
 * to anyone with DevTools — anyone could replay /api/sync/* writes. Replaced
 * with a Firebase ID token verified by FirebaseAuthMiddleware on the .NET side
 * (same pattern as src/sync/adminApi.js). The legacy header is gone entirely;
 * unauth users short-circuit before any network I/O.
 *
 * No singleton / no module-level state — every call resolves env + token fresh
 * so tests can stub `import.meta.env` / `projectAuth.currentUser` per test.
 */

import { getApiBaseUrl } from '@/sync/baseUrl';
import { getFirebaseTokenOrThrow, getFreshFirebaseTokenOrNull } from '@/sync/authToken';

const DEFAULT_TIMEOUT_MS = 5_000;

// Re-exported so existing callers (tests, sync.api.test.js) keep importing from
// '@/sync/api'. Canonical implementation lives in baseUrl.js — see comment there
// for the literal-access gotcha.
export { getApiBaseUrl };

// Token helpers live in authToken.js (shared with adminApi.js and signalrClient.js).
// getFirebaseTokenOrThrow(): hard fail with .isAuth = true — outbox defers on auth errors.
// getFreshFirebaseTokenOrNull(): force-refresh — used by file upload paths (long uploads
//   can outlive the 1h JWT TTL).

/**
 * POST a sync envelope to /api/sync/{collection}.
 *
 * @param {string} collection - e.g. 'teams', 'offers'
 * @param {object} envelope   - { id, op, payload, serverWriteTs }
 * @param {object} [opts]
 * @param {number} [opts.timeoutMs=5000]
 * @param {AbortSignal} [opts.signal] - external cancellation (e.g. tab unload)
 * @returns {Promise<object>} - parsed JSON response body
 * @throws {Error} on missing auth, network failure, timeout, or non-2xx response.
 *   `err.status` is set on HTTP errors; `err.isTimeout` on AbortError due to timeout;
 *   `err.isNetwork` on connection failures; `err.isAuth` on missing user or 401/403.
 */
export async function postSync(collection, envelope, opts = {}) {
  if (!collection || typeof collection !== 'string') {
    throw new Error('postSync: collection is required');
  }
  if (!envelope || typeof envelope !== 'object') {
    throw new Error('postSync: envelope is required');
  }

  // Resolve token BEFORE building the fetch — when the user is signed out we
  // must not even touch the network (and must not consume an outbox attempt).
  const token = await getFirebaseTokenOrThrow();

  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl.replace(/\/+$/, '')}/api/sync/${encodeURIComponent(collection)}`;

  const controller = new AbortController();
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = setTimeout(() => {
    controller.abort(new DOMException('Timeout', 'AbortError'));
  }, timeoutMs);

  // Chain external signal -> internal controller if caller passed one.
  if (opts.signal) {
    if (opts.signal.aborted) controller.abort(opts.signal.reason);
    else opts.signal.addEventListener('abort', () => controller.abort(opts.signal.reason), { once: true });
  }

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };

  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(envelope),
      signal: controller.signal,
      // mode default = cors; credentials default = same-origin (no cookies cross-site)
    });
  } catch (err) {
    clearTimeout(timer);
    if (err && err.name === 'AbortError') {
      const e = new Error(`postSync timeout after ${timeoutMs}ms (${collection})`);
      e.isTimeout = true;
      e.cause = err;
      throw e;
    }
    // Network error (DNS / connection refused / offline).
    const e = new Error(`postSync network failure (${collection}): ${err.message}`);
    e.isNetwork = true;
    e.cause = err;
    throw e;
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    let body = null;
    try { body = await res.text(); } catch (_) { /* swallow */ }
    const e = new Error(`postSync HTTP ${res.status} (${collection}): ${body || res.statusText}`);
    e.status = res.status;
    e.body = body;
    // 401/403 mean the JWT was rejected or the user lacks the role — same
    // transient-defer semantics as no-current-user, so the outbox does not
    // burn attempts on it.
    if (res.status === 401 || res.status === 403) e.isAuth = true;
    throw e;
  }

  try {
    return await res.json();
  } catch (_) {
    // 200 with no body — acceptable.
    return null;
  }
}

// ─── Etap 6 PR2 — file upload/download/delete helpers ────────────────────────
//
// These wrap the new backend file endpoints (POST /api/files/upload,
// GET /api/files/{id}, DELETE /api/files/{id}) and force a Firebase JWT
// refresh on every call. The force-refresh matters most for `postFile()`
// — large uploads on slow connections can exceed the 1h JWT TTL, so we
// grab a fresh token right before opening the request (Etap 6 open risk #1).
//
// `postFile()` uses XHR (not fetch) to expose upload progress via
// `xhr.upload.onprogress` — fetch's ReadableStream upload progress is still
// not universally supported across the browsers we care about (Chrome 124+
// only, no Firefox/Safari at time of writing).

/**
 * POST multipart upload to a backend file endpoint.
 *
 * @param {string} path        e.g. '/api/files/upload'
 * @param {File|Blob} file     the file payload (uses `file.name` for the form part)
 * @param {object} [fields]    extra form fields appended after the file part
 * @param {object} [opts]
 * @param {(pct: number) => void} [opts.onProgress] called with 0..100 during upload
 * @returns {Promise<object>}  parsed JSON response body (e.g. { id, url, filename, ... })
 */
export async function postFile(path, file, fields = {}, { onProgress } = {}) {
  const fd = new FormData();
  fd.append('file', file, file.name);
  Object.entries(fields).forEach(([k, v]) => fd.append(k, String(v)));
  const token = await getFreshFirebaseTokenOrNull();
  if (!token) throw new Error('Brak Firebase JWT — zaloguj się ponownie.');
  const url = getApiBaseUrl().replace(/\/+$/, '') + path;
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    if (xhr.upload) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && typeof onProgress === 'function') {
          onProgress((e.loaded / e.total) * 100);
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? JSON.parse(xhr.responseText) : null);
        } catch (err) {
          reject(new Error('Niepoprawna odpowiedź serwera: ' + xhr.responseText));
        }
      } else {
        reject(new Error(`${xhr.status}: ${xhr.responseText || xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Błąd sieci podczas uploadu'));
    xhr.send(fd);
  });
}

/**
 * GET a file blob from the backend (JWT-gated).
 * Used by `useStorage.downloadFile()` for VPS-stored files.
 *
 * @param {string} id   file id (offer_files.id)
 * @returns {Promise<Blob>}
 */
export async function getFileBlob(id) {
  const token = await getFreshFirebaseTokenOrNull();
  if (!token) throw new Error('Brak Firebase JWT');
  const url = getApiBaseUrl().replace(/\/+$/, '') + `/api/files/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Pobieranie pliku zwróciło ${res.status}`);
  return await res.blob();
}

/**
 * DELETE a file (soft delete on the backend).
 *
 * @param {string} id   file id
 * @returns {Promise<void>}
 */
export async function deleteFile(id) {
  const token = await getFreshFirebaseTokenOrNull();
  if (!token) throw new Error('Brak Firebase JWT');
  const url = getApiBaseUrl().replace(/\/+$/, '') + `/api/files/${encodeURIComponent(id)}`;
  const res = await fetch(url, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`Delete zwróciło ${res.status}`);
}
